import PropTypes from "prop-types";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  ArrowRight,
  Building2,
  Calendar,
  PauseCircle,
  Repeat,
  Users,
} from "lucide-react";

import { MetricCard } from "../../../components/MetricCard";
import { Button } from "../../../components/Button";
import { DataTable } from "../../../components/DataTable";
import { useTheme } from "../../../theme/useTheme";

const EmptyCardState = ({ text }) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: "1px dashed",
      borderColor: "divider",
      px: 2,
      py: 3,
      textAlign: "center",
      color: "text.secondary",
      fontSize: "0.875rem",
    }}
  >
    {text}
  </Box>
);

EmptyCardState.propTypes = {
  text: PropTypes.string.isRequired,
};

export function RecurrentProjectsKpiView({ data, isLoading, onViewDetails }) {
  const { colors, neutrals, theme } = useTheme();
  const summary = data?.summary || {};
  const byAgency = Array.isArray(data?.by_agency) ? data.by_agency : [];

  const summaryCards = [
    {
      key: "total-recurrent-projects",
      icon: Repeat,
      value: summary.total_recurrent_projects || 0,
      label: "Proyectos recurrentes generados",
      iconColor: colors.primary.main,
      iconBackgroundColor: `${colors.primary.main}15`,
    },
    {
      key: "generated-this-month",
      icon: Calendar,
      value: summary.generated_this_month || 0,
      label: "Generados este mes",
      iconColor: neutrals.gray[700],
      iconBackgroundColor: neutrals.gray[100],
    },
    {
      key: "agencies-with-active-recurrence",
      icon: Building2,
      value: summary.agencies_with_active_recurring_clients || 0,
      label: "Agencias con recurrencia activa",
      iconColor: colors.info.main,
      iconBackgroundColor: `${colors.info.main}15`,
    },
    {
      key: "active-clients",
      icon: Users,
      value: summary.active_recurring_clients || 0,
      label: "Clientes con recurrencia activa",
      iconColor: colors.success.main,
      iconBackgroundColor: `${colors.success.main}15`,
    },
    {
      key: "paused-clients",
      icon: PauseCircle,
      value: summary.paused_recurring_clients || 0,
      label: "Clientes en pausa",
      iconColor: colors.warning.main,
      iconBackgroundColor: `${colors.warning.main}15`,
    },
  ];

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 1.5, md: 2 },
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },
        }}
      >
        {summaryCards.map((card) => (
          <Box key={card.key}>
            <MetricCard
              icon={card.icon}
              value={card.value}
              label={card.label}
              isLoading={isLoading}
              iconColor={card.iconColor}
              iconBackgroundColor={card.iconBackgroundColor}
            />
          </Box>
        ))}
      </Box>

      {isLoading ? (
        Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={`agency-kpi-skeleton-${index}`}
            variant="rounded"
            height={78}
          />
        ))
      ) : byAgency.length === 0 ? (
        <EmptyCardState text="No hay agencias con datos de recurrencia para los filtros actuales." />
      ) : (
        <DataTable
          getRowId={(agency) => agency.agency_id}
          rows={byAgency}
          tableMinWidth={920}
          columns={[
            {
              key: "agency",
              header: "Agencia",
              render: (agency) => (
                <>
                  <Typography sx={{ fontWeight: 600 }}>{agency.agency_name}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {agency.total_clients} clientes con recurrencia
                  </Typography>
                </>
              ),
            },
            {
              key: "active_clients",
              header: "Activos",
              align: "center",
              cellSx: { fontWeight: 600 },
              render: (agency) => agency.active_clients,
            },
            {
              key: "paused_clients",
              header: "Pausados",
              align: "center",
              cellSx: { fontWeight: 600 },
              render: (agency) => agency.paused_clients,
            },
            {
              key: "total_recurrent_projects",
              header: "Proyectos",
              align: "center",
              cellSx: { fontWeight: 600 },
              render: (agency) => agency.total_recurrent_projects,
            },
            {
              key: "actions",
              header: "",
              align: "right",
              width: "1%",
              render: (agency) => (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(agency)}
                  sx={{
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                    borderColor: neutrals.gray[200],
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  VER DETALLES
                  <ArrowRight size={14} />
                </Button>
              ),
            },
          ]}
        />
      )}
    </Stack>
  );
}

RecurrentProjectsKpiView.propTypes = {
  data: PropTypes.shape({
    summary: PropTypes.object,
    by_agency: PropTypes.arrayOf(
      PropTypes.shape({
        agency_id: PropTypes.number,
        agency_name: PropTypes.string,
        total_clients: PropTypes.number,
        active_clients: PropTypes.number,
        paused_clients: PropTypes.number,
        total_recurrent_projects: PropTypes.number,
      })
    ),
  }),
  isLoading: PropTypes.bool,
  onViewDetails: PropTypes.func,
};

RecurrentProjectsKpiView.defaultProps = {
  data: null,
  isLoading: false,
  onViewDetails: () => {},
};
